/**
 * PRD-017 §3.14 links on a work item (QA-29; Sean 2026-09-13 21:59: links now, attachments after the document
 * provider). One rule shared by the New work item dialog, the drawer and the API: an http(s) URL of at most
 * 2000 characters, an optional label of at most 200. A Dropbox share link is the expected interim for documents
 * (PRD-021 decision); nothing here fetches the URL.
 */

export const WORK_ITEM_LINK_URL_MAX = 2000
export const WORK_ITEM_LINK_LABEL_MAX = 200
export const WORK_ITEM_LINKS_MAX = 20

export interface WorkItemLinkInput {
  readonly url: string
  readonly label?: string | null
}

/** Trimmed URL when it parses as http(s) and fits; null otherwise (javascript:, file:, ftp:, bare words, empty). */
export function normalizeWorkItemLinkUrl(url: string | null | undefined): string | null {
  const s = (url ?? '').trim()
  if (s.length === 0 || s.length > WORK_ITEM_LINK_URL_MAX)
    return null
  try {
    const u = new URL(s)
    if (u.protocol !== 'https:' && u.protocol !== 'http:')
      return null
    if (!u.hostname)
      return null
    return s
  }
  catch {
    return null
  }
}

export function isWorkItemLinkUrlValid(url: string | null | undefined): boolean {
  return normalizeWorkItemLinkUrl(url) !== null
}

/** Trimmed label or null when blank; a label over the cap is a problem, not silently cut. */
export function normalizeWorkItemLinkLabel(label: string | null | undefined): string | null {
  const s = (label ?? '').trim()
  return s.length === 0 ? null : s
}

/** Human message for the inline hint / the 422, or null when the link passes. */
export function workItemLinkProblem(input: WorkItemLinkInput): string | null {
  const url = (input.url ?? '').trim()
  if (url.length === 0)
    return 'Paste a link (https://…).'
  if (url.length > WORK_ITEM_LINK_URL_MAX)
    return `The link is over ${WORK_ITEM_LINK_URL_MAX} characters.`
  if (normalizeWorkItemLinkUrl(url) === null)
    return 'Links start with http:// or https://.'
  const label = normalizeWorkItemLinkLabel(input.label)
  if (label && label.length > WORK_ITEM_LINK_LABEL_MAX)
    return `The label is over ${WORK_ITEM_LINK_LABEL_MAX} characters.`
  return null
}

/** The host a link points at, for the compact display ("dropbox.com"); the raw URL when it does not parse. */
export function workItemLinkHost(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '')
  }
  catch {
    return url
  }
}
